export interface LightspeedError {
  code: string;
  message: string;
  details?: any;
  userMessage: string;
  retryable: boolean;
}

export enum LightspeedErrorCodes {
  // OAuth Errors
  OAUTH_INVALID_CODE = 'OAUTH_INVALID_CODE',
  OAUTH_EXPIRED_CODE = 'OAUTH_EXPIRED_CODE',
  OAUTH_INVALID_STATE = 'OAUTH_INVALID_STATE',
  OAUTH_ACCESS_DENIED = 'OAUTH_ACCESS_DENIED',
  OAUTH_SERVER_ERROR = 'OAUTH_SERVER_ERROR',
  
  // Connection Errors
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  CONNECTION_TIMEOUT = 'CONNECTION_TIMEOUT',
  CONNECTION_UNAUTHORIZED = 'CONNECTION_UNAUTHORIZED',
  CONNECTION_FORBIDDEN = 'CONNECTION_FORBIDDEN',
  
  // API Errors
  API_RATE_LIMITED = 'API_RATE_LIMITED',
  API_INVALID_REQUEST = 'API_INVALID_REQUEST',
  API_SERVER_ERROR = 'API_SERVER_ERROR',
  API_SERVICE_UNAVAILABLE = 'API_SERVICE_UNAVAILABLE',
  
  // Store Errors
  STORE_NOT_FOUND = 'STORE_NOT_FOUND',
  STORE_ALREADY_CONNECTED = 'STORE_ALREADY_CONNECTED',
  STORE_DISCONNECTION_FAILED = 'STORE_DISCONNECTION_FAILED',
  
  // Installation Errors
  INSTALLATION_PENDING = 'INSTALLATION_PENDING',
  INSTALLATION_FAILED = 'INSTALLATION_FAILED',
  INSTALLATION_TIMEOUT = 'INSTALLATION_TIMEOUT',
  
  // Network Errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  
  // Unknown Error
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export const LightspeedErrorMessages: Record<LightspeedErrorCodes, { message: string; userMessage: string; retryable: boolean }> = {
  [LightspeedErrorCodes.OAUTH_INVALID_CODE]: {
    message: 'Invalid OAuth authorization code',
    userMessage: 'The authorization code is invalid. Please try connecting again.',
    retryable: true
  },
  [LightspeedErrorCodes.OAUTH_EXPIRED_CODE]: {
    message: 'OAuth authorization code has expired',
    userMessage: 'The authorization code has expired. Please try connecting again.',
    retryable: true
  },
  [LightspeedErrorCodes.OAUTH_INVALID_STATE]: {
    message: 'Invalid OAuth state parameter',
    userMessage: 'Security validation failed. Please try connecting again.',
    retryable: true
  },
  [LightspeedErrorCodes.OAUTH_ACCESS_DENIED]: {
    message: 'OAuth access denied by user',
    userMessage: 'Access was denied. Please grant permission to connect your Lightspeed store.',
    retryable: true
  },
  [LightspeedErrorCodes.OAUTH_SERVER_ERROR]: {
    message: 'OAuth server error',
    userMessage: 'Lightspeed server error occurred. Please try again later.',
    retryable: true
  },
  [LightspeedErrorCodes.CONNECTION_FAILED]: {
    message: 'Failed to establish connection with Lightspeed',
    userMessage: 'Unable to connect to Lightspeed. Please check your internet connection and try again.',
    retryable: true
  },
  [LightspeedErrorCodes.CONNECTION_TIMEOUT]: {
    message: 'Connection to Lightspeed timed out',
    userMessage: 'Connection timed out. Please try again.',
    retryable: true
  },
  [LightspeedErrorCodes.CONNECTION_UNAUTHORIZED]: {
    message: 'Unauthorized access to Lightspeed API',
    userMessage: 'Your Lightspeed connection has expired. Please reconnect your store.',
    retryable: true
  },
  [LightspeedErrorCodes.CONNECTION_FORBIDDEN]: {
    message: 'Access forbidden to Lightspeed resource',
    userMessage: 'Access denied. Please ensure you have the necessary permissions.',
    retryable: false
  },
  [LightspeedErrorCodes.API_RATE_LIMITED]: {
    message: 'Lightspeed API rate limit exceeded',
    userMessage: 'Too many requests. Please wait a moment and try again.',
    retryable: true
  },
  [LightspeedErrorCodes.API_INVALID_REQUEST]: {
    message: 'Invalid request to Lightspeed API',
    userMessage: 'Invalid request. Please contact support if this persists.',
    retryable: false
  },
  [LightspeedErrorCodes.API_SERVER_ERROR]: {
    message: 'Lightspeed API server error',
    userMessage: 'Lightspeed server error. Please try again later.',
    retryable: true
  },
  [LightspeedErrorCodes.API_SERVICE_UNAVAILABLE]: {
    message: 'Lightspeed API service unavailable',
    userMessage: 'Lightspeed service is temporarily unavailable. Please try again later.',
    retryable: true
  },
  [LightspeedErrorCodes.STORE_NOT_FOUND]: {
    message: 'Lightspeed store not found',
    userMessage: 'Store not found. Please verify your store information.',
    retryable: false
  },
  [LightspeedErrorCodes.STORE_ALREADY_CONNECTED]: {
    message: 'Store is already connected to Lightspeed',
    userMessage: 'Your store is already connected to Lightspeed.',
    retryable: false
  },
  [LightspeedErrorCodes.STORE_DISCONNECTION_FAILED]: {
    message: 'Failed to disconnect store from Lightspeed',
    userMessage: 'Unable to disconnect your store. Please try again or contact support.',
    retryable: true
  },
  [LightspeedErrorCodes.INSTALLATION_PENDING]: {
    message: 'Lightspeed installation is still pending',
    userMessage: 'Installation is still in progress. Please wait a moment.',
    retryable: true
  },
  [LightspeedErrorCodes.INSTALLATION_FAILED]: {
    message: 'Lightspeed installation failed',
    userMessage: 'Installation failed. Please try connecting again.',
    retryable: true
  },
  [LightspeedErrorCodes.INSTALLATION_TIMEOUT]: {
    message: 'Lightspeed installation timed out',
    userMessage: 'Installation timed out. Please try again.',
    retryable: true
  },
  [LightspeedErrorCodes.NETWORK_ERROR]: {
    message: 'Network error occurred',
    userMessage: 'Network error. Please check your internet connection and try again.',
    retryable: true
  },
  [LightspeedErrorCodes.NETWORK_TIMEOUT]: {
    message: 'Network request timed out',
    userMessage: 'Request timed out. Please try again.',
    retryable: true
  },
  [LightspeedErrorCodes.UNKNOWN_ERROR]: {
    message: 'Unknown error occurred',
    userMessage: 'An unexpected error occurred. Please try again or contact support.',
    retryable: true
  }
};

export class LightspeedErrorHandler {
  static mapHttpErrorToLightspeedError(error: any): LightspeedError {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const message = error?.message || 'Unknown error';

    // Handle specific HTTP status codes
    switch (status) {
      case 400:
        if (data?.error === 'invalid_grant' || data?.error_description?.includes('code')) {
          return this.createError(LightspeedErrorCodes.OAUTH_INVALID_CODE, data);
        }
        if (data?.error === 'invalid_request') {
          return this.createError(LightspeedErrorCodes.API_INVALID_REQUEST, data);
        }
        return this.createError(LightspeedErrorCodes.API_INVALID_REQUEST, data);

      case 401:
        if (data?.error === 'access_denied') {
          return this.createError(LightspeedErrorCodes.OAUTH_ACCESS_DENIED, data);
        }
        return this.createError(LightspeedErrorCodes.CONNECTION_UNAUTHORIZED, data);

      case 403:
        return this.createError(LightspeedErrorCodes.CONNECTION_FORBIDDEN, data);

      case 404:
        return this.createError(LightspeedErrorCodes.STORE_NOT_FOUND, data);

      case 409:
        return this.createError(LightspeedErrorCodes.STORE_ALREADY_CONNECTED, data);

      case 429:
        return this.createError(LightspeedErrorCodes.API_RATE_LIMITED, data);

      case 500:
        return this.createError(LightspeedErrorCodes.API_SERVER_ERROR, data);

      case 503:
        return this.createError(LightspeedErrorCodes.API_SERVICE_UNAVAILABLE, data);

      default:
        // Handle network errors
        if (error?.code === 'NETWORK_ERROR' || error?.code === 'ERR_NETWORK') {
          return this.createError(LightspeedErrorCodes.NETWORK_ERROR, error);
        }
        
        if (error?.code === 'ECONNABORTED' || message.includes('timeout')) {
          return this.createError(LightspeedErrorCodes.NETWORK_TIMEOUT, error);
        }

        return this.createError(LightspeedErrorCodes.UNKNOWN_ERROR, error);
    }
  }

  static mapOAuthErrorToLightspeedError(error: string, errorDescription?: string): LightspeedError {
    switch (error) {
      case 'access_denied':
        return this.createError(LightspeedErrorCodes.OAUTH_ACCESS_DENIED);
      case 'invalid_request':
        return this.createError(LightspeedErrorCodes.OAUTH_INVALID_CODE);
      case 'invalid_grant':
        if (errorDescription?.includes('expired')) {
          return this.createError(LightspeedErrorCodes.OAUTH_EXPIRED_CODE);
        }
        return this.createError(LightspeedErrorCodes.OAUTH_INVALID_CODE);
      case 'server_error':
        return this.createError(LightspeedErrorCodes.OAUTH_SERVER_ERROR);
      default:
        return this.createError(LightspeedErrorCodes.UNKNOWN_ERROR, { error, errorDescription });
    }
  }

  static createError(code: LightspeedErrorCodes, details?: any): LightspeedError {
    const errorInfo = LightspeedErrorMessages[code];
    return {
      code,
      message: errorInfo.message,
      userMessage: errorInfo.userMessage,
      retryable: errorInfo.retryable,
      details
    };
  }

  static getRetryDelay(error: LightspeedError, attemptNumber: number): number {
    if (!error.retryable) return 0;

    // Exponential backoff with jitter
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, attemptNumber - 1), maxDelay);
    
    // Add jitter (±25%)
    const jitter = delay * 0.25 * (Math.random() * 2 - 1);
    return Math.max(0, delay + jitter);
  }

  static shouldRetry(error: LightspeedError, attemptNumber: number, maxRetries: number = 3): boolean {
    return error.retryable && attemptNumber < maxRetries;
  }
}

// Helper function to handle errors in components
export const handleLightspeedError = (error: any): LightspeedError => {
  if (error?.code && Object.values(LightspeedErrorCodes).includes(error.code)) {
    return error as LightspeedError;
  }
  
  return LightspeedErrorHandler.mapHttpErrorToLightspeedError(error);
};

// Helper function for displaying error messages to users
export const getLightspeedErrorMessage = (error: any): string => {
  const lightspeedError = handleLightspeedError(error);
  return lightspeedError.userMessage;
};

// Helper function to check if an error is retryable
export const isLightspeedErrorRetryable = (error: any): boolean => {
  const lightspeedError = handleLightspeedError(error);
  return lightspeedError.retryable;
};